import React from "react";
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import Groups2RoundedIcon from "@mui/icons-material/Groups2Rounded";
import HealthAndSafetyRoundedIcon from "@mui/icons-material/HealthAndSafetyRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import SeoHead, { SITE_URL } from "../seo/SeoHead";
import trustBudMark from "../assets/logo/trustbud-mark.png";

const colors = {
  navy950: "#071527",
  navy900: "#0c1a2e",
  navy800: "#102544",
  navy700: "#17345d",
  teal: "#2e8b76",
  green: "#2d8a5f",
  amber: "#b56b22",
  text: "#102033",
  secondary: "#405873",
  muted: "#71849a",
  border: "#d7e2ee",
  panel: "#ffffff",
  canvas: "#f3f6fa",
};

const sharedFaq = [
  [
    "Can I use TrustBud without a group?",
    "Yes. Free users can start with one one-on-one Accountability agreement and use Daily Reports to stay visible to one person.",
  ],
  [
    "Does TrustBud share private proof?",
    "Proof is private by default. Other people see the safe report state unless you choose to make proof details visible.",
  ],
  [
    "What happens when I miss?",
    "A missed report becomes No report. TrustBud then gives you a clean return path with Reset, a new report, or a smaller plan change.",
  ],
];

const pageDefinitions = {
  accountability: {
    key: "accountability",
    path: "/accountability",
    metaTitle: "Accountability App for Habits and Goals | TrustBud",
    metaDescription:
      "TrustBud is an accountability app with Daily Reports, proof, visible No report days, groups, and recovery paths for habits and goals that matter.",
    eyebrow: "Accountability app",
    headline: "Daily accountability that makes follow-through visible.",
    subhead:
      "TrustBud helps you create a clear agreement, submit a Daily Report, attach proof when it matters, and return quickly after messy days.",
    primaryKeyword: "accountability app",
    stats: ["Daily Reports", "Proof options", "No report visibility"],
    bullets: [
      "One-on-one or group Accountability",
      "Done, Reset, and No report states",
      "Private proof controls for sensitive habits",
      "Activity updates that show what needs a response",
    ],
    mechanismTitle: "A simple loop for staying visible",
    mechanismBody:
      "Choose what you are reporting on, set a deadline, invite the right person or group, and submit a report each day. Silence becomes visible without turning the product into a public feed.",
    proofTitle: "What others can see",
    proofItems: ["Report submitted", "Reset submitted", "No report", "Proof attached"],
    useCases: [
      ["Fitness follow-through", "Use proof and Daily Reports when workouts keep slipping."],
      ["Study consistency", "Let one person see whether the report came in before the deadline."],
      ["Personal commitments", "Keep private details private while making follow-through visible."],
    ],
    recommendation:
      "Start with one person and one clear Daily Report. Add proof only when visibility alone is not enough.",
    faqs: sharedFaq,
  },
  habitTrackerWithAccountability: {
    key: "habitTrackerWithAccountability",
    path: "/habit-tracker-with-accountability",
    metaTitle: "Habit Tracker With Accountability Partner | TrustBud",
    metaDescription:
      "TrustBud combines habit tracking with accountability partners, Daily Reports, proof, and recovery support so missed days do not turn into avoidance.",
    eyebrow: "Habit tracker with accountability",
    headline: "Track the habit and keep the agreement visible.",
    subhead:
      "TrustBud keeps Today action-first, then adds the social layer that normal habit trackers usually miss: someone can see whether you reported.",
    primaryKeyword: "habit tracker with accountability partner",
    stats: ["Today execution", "Partner visibility", "Recovery after misses"],
    bullets: [
      "Today shows what is due now",
      "Accountability shows whether the report came in",
      "Plan Health helps keep the plan realistic",
      "Intel and Analytics watch what actually helps",
    ],
    mechanismTitle: "From checklist to follow-through system",
    mechanismBody:
      "The habit list stays focused on execution. Accountability adds a Daily Report, proof when useful, and a person or group who can respond when the day starts slipping.",
    proofTitle: "Useful accountability signals",
    proofItems: ["Done habits", "Reset reports", "Proof attached", "Quiet days"],
    useCases: [
      ["Workout habits", "Connect workouts to a visible report that another person can see."],
      ["Language learning", "Use a simple report deadline when consistency matters more than intensity."],
      ["Creative work", "Keep building sessions visible without broadcasting every detail."],
    ],
    recommendation:
      "Use TrustBud when private tracking is not enough and you need one person to notice whether you showed up.",
    faqs: sharedFaq,
  },
  groupAccountability: {
    key: "groupAccountability",
    path: "/group-accountability-app",
    metaTitle: "Group Accountability App With Daily Reports | TrustBud",
    metaDescription:
      "Create group accountability with a daily board, report deadlines, proof options, leaderboards, and privacy-safe activity updates in TrustBud.",
    eyebrow: "Group accountability",
    headline: "A daily board for the people trying to stay consistent together.",
    subhead:
      "TrustBud groups show who reported, who reset, who still has an open report, and whether everyone showed up today.",
    primaryKeyword: "group accountability app",
    stats: ["Group board", "Leaderboard", "All-in days"],
    bullets: [
      "Accepted members owe reports; pending invites do not",
      "Leaderboards emphasize report days and streaks",
      "No report is visible after the deadline",
      "Sensitive details stay hidden unless visibility allows them",
    ],
    mechanismTitle: "Social pressure without a noisy feed",
    mechanismBody:
      "The group page centers the Daily Report board, the weekly leaderboard, the log, and quick responses. Chat stays secondary to the accountability moment.",
    proofTitle: "Group board states",
    proofItems: ["Done", "Reset", "Open", "No report"],
    useCases: [
      ["Fitness groups", "Use proof and a weekly board to keep workouts visible."],
      ["Study groups", "Make daily reports visible before deadlines."],
      ["Builder groups", "Keep shipping or writing sessions from going quiet."],
    ],
    recommendation:
      "Use a group when the shared board, leaderboard, and all-in day effect would create useful pressure.",
    faqs: sharedFaq,
  },
  dailyReportHabitTracker: {
    key: "dailyReportHabitTracker",
    path: "/daily-report-habit-tracker",
    metaTitle: "Daily Report Habit Tracker for Accountability | TrustBud",
    metaDescription:
      "TrustBud uses Daily Reports to show Done, Reset, and No report states, helping habit follow-through stay visible and recoverable.",
    eyebrow: "Daily report habit tracker",
    headline: "Every day gets a clear ending.",
    subhead:
      "A Daily Report turns habit follow-through into a visible state: Done, Reset, or No report. That makes recovery faster when the plan gets messy.",
    primaryKeyword: "daily report habit tracker",
    stats: ["Done", "Reset", "No report"],
    bullets: [
      "Done means the commitment was completed",
      "Reset means the full thing slipped but you stayed visible",
      "No report means nothing was submitted by the deadline",
      "Daily Reports can be satisfied from Today when the habit is completed",
    ],
    mechanismTitle: "A report rhythm that reduces avoidance",
    mechanismBody:
      "The report creates a small daily moment of honesty. If the full habit does not happen, Reset gives the user a way to stay connected and return quickly.",
    proofTitle: "Report evidence",
    proofItems: ["Today completion", "Note proof", "Apple Health proof", "Partner acknowledgment"],
    useCases: [
      ["Evening review", "Close the loop before the deadline."],
      ["Quiet day recovery", "Use Reset to stay visible."],
      ["Proof-required habits", "Attach proof for habits that need stronger accountability."],
    ],
    recommendation:
      "Use Daily Reports for goals where the biggest risk is avoiding the plan after an imperfect day.",
    faqs: sharedFaq,
  },
  appleHealthProof: {
    key: "appleHealthProof",
    path: "/apple-health-habit-proof",
    metaTitle: "Apple Health Habit Proof for Accountability | TrustBud",
    metaDescription:
      "TrustBud can use Apple Health and Apple Watch activity as privacy-safe proof for matching fitness and movement habits when the user allows it.",
    eyebrow: "Apple Health proof",
    headline: "Use Apple Health as proof when it matches the habit.",
    subhead:
      "For walking, workouts, steps, distance, and active energy habits, TrustBud can help attach Apple Health evidence without exposing raw health details by default.",
    primaryKeyword: "Apple Health habit proof",
    stats: ["Workout proof", "Steps proof", "Private by default"],
    bullets: [
      "Apple Health proof is user-confirmed",
      "Other people see a safe proof label by default",
      "Fitness habits can be matched to relevant health signals",
      "Raw health details are not pushed into social surfaces by default",
    ],
    mechanismTitle: "Proof that reduces manual work",
    mechanismBody:
      "When TrustBud detects matching Apple Health data, the user can choose to attach it to an Accountability report. The proof strengthens the report while keeping details controlled.",
    proofTitle: "Supported proof signals",
    proofItems: ["Steps", "Workouts", "Active energy", "Walking or running distance"],
    useCases: [
      ["Walking goals", "Attach a matching walk as proof."],
      ["Steps goals", "Use step count evidence when available."],
      ["Workout habits", "Use Apple Watch workout data for stronger accountability."],
    ],
    recommendation:
      "Use Apple Health proof for movement and fitness habits where the device signal clearly matches the commitment.",
    faqs: [
      ...sharedFaq,
      [
        "Does TrustBud complete habits automatically from Apple Health?",
        "First-pass Health proof requires confirmation. The user chooses whether to use detected activity as proof.",
      ],
    ],
  },
  fitnessGoals: {
    key: "fitnessGoals",
    path: "/for-fitness-goals",
    metaTitle: "Accountability for Fitness Goals | TrustBud",
    metaDescription:
      "TrustBud helps fitness goals stay visible with habits, Daily Reports, proof options, Reset, group accountability, and Apple Health support.",
    eyebrow: "Fitness accountability",
    headline: "Make fitness follow-through harder to ignore.",
    subhead:
      "Turn fitness goals into due habits, report what happened, attach proof when useful, and recover quickly after missed days.",
    primaryKeyword: "accountability for fitness goals",
    stats: ["Workout habits", "Proof", "Reset"],
    bullets: [
      "Use Today for the workout action",
      "Use Accountability for the report deadline",
      "Use proof for fitness habits that need credibility",
      "Use Reset to stay connected after imperfect days",
    ],
    mechanismTitle: "Fitness habits need visibility and recovery",
    mechanismBody:
      "TrustBud keeps the next workout clear, makes the report visible to the right people, and uses proof or Apple Health when it strengthens accountability.",
    proofTitle: "Fitness proof options",
    proofItems: ["Today completion", "Apple Health", "Photo proof", "Note proof"],
    useCases: [
      ["Walking", "Keep a daily or weekly walking habit visible."],
      ["Strength training", "Schedule lifting days without counting off-days as failures."],
      ["Weight goals", "Track the habits behind the goal without sharing sensitive details by default."],
    ],
    recommendation:
      "Start with one fitness habit and one report deadline. Add proof if the habit has been easy to avoid.",
    faqs: sharedFaq,
  },
  students: {
    key: "students",
    path: "/for-students",
    metaTitle: "Accountability App for Students and Study Habits | TrustBud",
    metaDescription:
      "TrustBud helps students turn study goals into daily habits, reports, accountability, proof, and weekly review without turning the app into a social feed.",
    eyebrow: "Student accountability",
    headline: "Build study consistency before the deadline gets close.",
    subhead:
      "TrustBud helps students report study sessions, stay visible to a partner or group, and recover before one missed day becomes a lost week.",
    primaryKeyword: "accountability app for students",
    stats: ["Study reports", "Group accountability", "Weekly review"],
    bullets: [
      "Set recurring study habits",
      "Report Done or Reset before the deadline",
      "Use a small group for shared visibility",
      "Review which times and days actually worked",
    ],
    mechanismTitle: "A study loop that catches avoidance early",
    mechanismBody:
      "The daily report creates a clear endpoint for each study day. Accountability makes silence visible, and Weekly Review helps adjust the plan before the next week starts.",
    proofTitle: "Study accountability signals",
    proofItems: ["Report submitted", "Study note", "Timer proof", "Group board"],
    useCases: [
      ["Exam prep", "Make each study block visible before the final week."],
      ["Language learning", "Keep short daily practice from disappearing."],
      ["Reading plans", "Use Reset when the full session does not happen."],
    ],
    recommendation:
      "Use TrustBud for study routines where a visible report would make it easier to start sooner.",
    faqs: sharedFaq,
  },
  creatorsBuilders: {
    key: "creatorsBuilders",
    path: "/for-creators-builders",
    metaTitle: "Accountability for Creators, Builders, and Side Projects | TrustBud",
    metaDescription:
      "TrustBud helps creators and builders keep writing, shipping, and project habits visible through Daily Reports, proof, groups, and recovery.",
    eyebrow: "Creator accountability",
    headline: "Keep building when the project gets quiet.",
    subhead:
      "TrustBud helps turn creative and building goals into visible sessions, honest reports, and quick recovery when momentum slips.",
    primaryKeyword: "accountability for creators",
    stats: ["Project habits", "Daily reports", "Group pressure"],
    bullets: [
      "Report writing, shipping, or building sessions",
      "Use proof without sharing sensitive project details",
      "Let a partner see whether the report came in",
      "Use Weekly Review to keep the plan realistic",
    ],
    mechanismTitle: "Visibility for work that is easy to postpone",
    mechanismBody:
      "Creative work often slips because there is no clear external signal. TrustBud gives the work a deadline, a report, and a recovery path.",
    proofTitle: "Builder proof examples",
    proofItems: ["Today completion", "Progress note", "Photo proof", "Report streak"],
    useCases: [
      ["Writing", "Report sessions without publishing drafts."],
      ["Side projects", "Keep shipping sessions visible."],
      ["Business building", "Use a group to maintain weekly pressure."],
    ],
    recommendation:
      "Use Accountability for the sessions that are easy to defer because nobody else can see them.",
    faqs: sharedFaq,
  },
  spiritualConsistency: {
    key: "spiritualConsistency",
    path: "/for-spiritual-consistency",
    metaTitle: "Accountability for Spiritual Habits | TrustBud",
    metaDescription:
      "TrustBud helps spiritual habits stay consistent with private Daily Reports, gentle accountability, Reset, and weekly reflection.",
    eyebrow: "Spiritual consistency",
    headline: "Keep spiritual habits connected to the life you are building.",
    subhead:
      "TrustBud helps make prayer, scripture study, journaling, service, or reflection visible enough to stay consistent while keeping details private.",
    primaryKeyword: "accountability for spiritual habits",
    stats: ["Private reports", "Gentle accountability", "Reset"],
    bullets: [
      "Use private Daily Reports for sensitive habits",
      "Invite one person who can respond with care",
      "Use Reset to stay connected after imperfect days",
      "Review timing patterns without exposing private details",
    ],
    mechanismTitle: "Accountability with privacy and care",
    mechanismBody:
      "The right person can see whether the report came in, while notes and proof stay private unless the user chooses to share them.",
    proofTitle: "Privacy-safe signals",
    proofItems: ["Report submitted", "Reset submitted", "Private note", "No report"],
    useCases: [
      ["Scripture study", "Make the routine visible without sharing the content."],
      ["Prayer or reflection", "Use the report state and keep private details protected."],
      ["Journaling", "Track consistency while protecting the actual journal entry."],
    ],
    recommendation:
      "Use one trusted person and status-only visibility when the habit is personal.",
    faqs: sharedFaq,
  },
};

const SEO_LANDING_PAGES = Object.values(pageDefinitions);

function buildJsonLd(page) {
  const canonical = `${SITE_URL}${page.path}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SoftwareApplication",
        name: "TrustBud",
        applicationCategory: "LifestyleApplication",
        operatingSystem: "iOS, Web",
        url: canonical,
        description: page.metaDescription,
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "USD",
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: page.faqs.map(([question, answer]) => ({
          "@type": "Question",
          name: question,
          acceptedAnswer: {
            "@type": "Answer",
            text: answer,
          },
        })),
      },
    ],
  };
}

function LogoLockup() {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box component="img" src={trustBudMark} alt="TrustBud" sx={{ width: 34, height: 34 }} />
      <Typography sx={{ color: colors.text, fontSize: "1.15rem", fontWeight: 900 }}>
        TrustBud
      </Typography>
    </Stack>
  );
}

function LandingHeader() {
  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: "rgba(255,255,255,0.94)",
        backdropFilter: "blur(18px)",
        borderBottom: `1px solid ${colors.border}`,
      }}
    >
      <Toolbar sx={{ minHeight: 68, justifyContent: "space-between", gap: 2, px: { xs: 1.5, md: 3 } }}>
        <Box component={RouterLink} to="/" sx={{ textDecoration: "none" }}>
          <LogoLockup />
        </Box>
        <Stack direction="row" spacing={{ xs: 0.5, sm: 1.25 }} alignItems="center">
          <Button
            component={RouterLink}
            to="/habit-tracker-with-accountability"
            sx={{ color: colors.secondary, display: { xs: "none", md: "inline-flex" } }}
          >
            How it works
          </Button>
          <Button component={RouterLink} to="/login" variant="text" size="small" sx={{ whiteSpace: "nowrap" }}>
            Sign in
          </Button>
          <Button component={RouterLink} to="/register" variant="contained" size="small" sx={{ whiteSpace: "nowrap" }}>
            Start
          </Button>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}

function ProofPanel({ page }) {
  return (
    <Card
      elevation={0}
      sx={{
        bgcolor: "#ffffff",
        border: `1px solid ${colors.border}`,
        boxShadow: "0 12px 30px rgba(15, 23, 42, 0.04)",
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Stack spacing={1.5}>
          <Typography sx={{ color: colors.text, fontWeight: 900 }}>{page.proofTitle}</Typography>
          <Grid container spacing={1}>
            {page.proofItems.map((item) => (
              <Grid item xs={12} sm={6} key={item}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    p: 1.15,
                    bgcolor: "#f8fafc",
                    border: `1px solid ${colors.border}`,
                    borderRadius: "8px",
                  }}
                >
                  <CheckCircleRoundedIcon sx={{ color: colors.green, fontSize: 20 }} />
                  <Typography sx={{ color: colors.text, fontWeight: 820 }}>{item}</Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
          <Typography sx={{ color: colors.muted, lineHeight: 1.6 }}>
            TrustBud keeps proof and sensitive details controlled. Report state can be visible while notes, photos, and health details stay private.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

function RelatedPages({ currentPath }) {
  const pages = SEO_LANDING_PAGES.filter((page) => page.path !== currentPath).slice(0, 5);
  return (
    <Stack spacing={1.1}>
      {pages.map((page) => (
        <Button
          key={page.path}
          component={RouterLink}
          to={page.path}
          variant="outlined"
          sx={{
            justifyContent: "space-between",
            color: colors.navy700,
            borderColor: colors.border,
            bgcolor: "#ffffff",
            textTransform: "none",
            fontWeight: 850,
          }}
        >
          {page.eyebrow}
        </Button>
      ))}
    </Stack>
  );
}

export default function SeoLandingPage({ pageKey }) {
  const location = useLocation();
  const page = pageDefinitions[pageKey] || SEO_LANDING_PAGES.find((item) => item.path === location.pathname) || pageDefinitions.accountability;
  const year = new Date().getFullYear();

  return (
    <Box sx={{ bgcolor: colors.canvas, color: colors.text }}>
      <SeoHead
        title={page.metaTitle}
        description={page.metaDescription}
        path={page.path}
        jsonLd={buildJsonLd(page)}
      />
      <LandingHeader />

      <Box component="main">
        <Box sx={{ bgcolor: colors.navy950, color: "#ffffff" }}>
          <Container maxWidth="lg" sx={{ py: { xs: 5.5, md: 8.5 } }}>
            <Grid container spacing={{ xs: 4, md: 7 }} alignItems="center">
              <Grid item xs={12} md={7}>
                <Stack spacing={3}>
                  <Chip
                    label={page.primaryKeyword}
                    sx={{
                      width: "fit-content",
                      maxWidth: "100%",
                      bgcolor: "rgba(255,255,255,0.08)",
                      color: "#ffffff",
                      border: "1px solid rgba(255,255,255,0.14)",
                      fontWeight: 850,
                      "& .MuiChip-label": {
                        whiteSpace: "normal",
                        lineHeight: 1.25,
                      },
                    }}
                  />
                  <Stack spacing={1.8}>
                    <Typography
                      variant="h1"
                      sx={{
                        color: "#ffffff",
                        fontWeight: 950,
                        letterSpacing: 0,
                        lineHeight: 1.02,
                        fontSize: { xs: "2.45rem", sm: "3.25rem", md: "4.25rem" },
                        maxWidth: 780,
                      }}
                    >
                      {page.headline}
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.78)", lineHeight: 1.65, fontSize: "1.12rem", maxWidth: 680 }}>
                      {page.subhead}
                    </Typography>
                  </Stack>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                    <Button
                      component={RouterLink}
                      to="/register"
                      variant="contained"
                      size="large"
                      sx={{ bgcolor: "#ffffff", color: colors.navy900, "&:hover": { bgcolor: "#e8eef5" } }}
                    >
                      Start your first habit
                    </Button>
                    <Button
                      component={RouterLink}
                      to="/login"
                      variant="outlined"
                      size="large"
                      sx={{
                        color: "#ffffff",
                        borderColor: "rgba(255,255,255,0.34)",
                        bgcolor: "rgba(255,255,255,0.04)",
                        "&:hover": { borderColor: "rgba(255,255,255,0.54)" },
                      }}
                    >
                      Sign in
                    </Button>
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12} md={5}>
                <Card
                  elevation={0}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.14)",
                    boxShadow: "0 18px 42px rgba(0,0,0,0.18)",
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack spacing={2}>
                      <Typography sx={{ color: "#ffffff", fontWeight: 900 }}>What TrustBud makes visible</Typography>
                      {page.stats.map((stat) => (
                        <Stack key={stat} direction="row" spacing={1.1} alignItems="center">
                          <CheckCircleRoundedIcon sx={{ color: "#8fd2c1", fontSize: 20 }} />
                          <Typography sx={{ color: "rgba(255,255,255,0.82)", fontWeight: 780 }}>{stat}</Typography>
                        </Stack>
                      ))}
                      <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />
                      <Typography sx={{ color: "#8fd2c1", fontWeight: 900 }}>
                        {page.recommendation}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: { xs: 5.5, md: 7 } }}>
          <Grid container spacing={2}>
            {page.bullets.map((item) => (
              <Grid item xs={12} md={3} key={item}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    bgcolor: "#ffffff",
                    border: `1px solid ${colors.border}`,
                    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.035)",
                  }}
                >
                  <CardContent sx={{ p: 2.2 }}>
                    <CheckCircleRoundedIcon sx={{ color: colors.green, mb: 1 }} />
                    <Typography sx={{ color: colors.text, fontWeight: 860, lineHeight: 1.35 }}>{item}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>

        <Box sx={{ bgcolor: "#ffffff", borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}` }}>
          <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
            <Grid container spacing={{ xs: 4, md: 7 }} alignItems="center">
              <Grid item xs={12} md={5.2}>
                <Stack spacing={1.4}>
                  <Typography variant="overline" sx={{ color: colors.teal, fontWeight: 900, letterSpacing: 0 }}>
                    How it works
                  </Typography>
                  <Typography variant="h2" sx={{ color: colors.text, fontWeight: 950, lineHeight: 1.08, fontSize: { xs: "2rem", md: "2.55rem" } }}>
                    {page.mechanismTitle}
                  </Typography>
                  <Typography sx={{ color: colors.secondary, lineHeight: 1.65 }}>
                    {page.mechanismBody}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6.8}>
                <Grid container spacing={1.5}>
                  {[
                    [<ScheduleRoundedIcon />, "Plan", "Choose the habit, cadence, and report deadline."],
                    [<Groups2RoundedIcon />, "Accountability", "Invite the person or group who should see the report state."],
                    [<HealthAndSafetyRoundedIcon />, "Proof", "Attach proof only when it makes the agreement stronger."],
                    [<InsightsRoundedIcon />, "Learn", "Use Analytics and Plan Health to adjust what is not working."],
                  ].map(([icon, title, body]) => (
                    <Grid item xs={12} sm={6} key={title}>
                      <Card
                        elevation={0}
                        sx={{
                          height: "100%",
                          bgcolor: "#f8fafc",
                          border: `1px solid ${colors.border}`,
                          boxShadow: "0 10px 24px rgba(15, 23, 42, 0.035)",
                        }}
                      >
                        <CardContent sx={{ p: 2.2 }}>
                          <Box sx={{ color: colors.teal, mb: 1 }}>{icon}</Box>
                          <Typography sx={{ color: colors.text, fontWeight: 900 }}>{title}</Typography>
                          <Typography sx={{ color: colors.secondary, lineHeight: 1.55, mt: 0.5 }}>{body}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Grid container spacing={{ xs: 4, md: 7 }} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <ProofPanel page={page} />
            </Grid>
            <Grid item xs={12} md={7}>
              <Stack spacing={2}>
                <Typography variant="overline" sx={{ color: colors.teal, fontWeight: 900, letterSpacing: 0 }}>
                  Use cases
                </Typography>
                <Typography variant="h2" sx={{ color: colors.text, fontWeight: 950, lineHeight: 1.08, fontSize: { xs: "2rem", md: "2.45rem" } }}>
                  Where this helps
                </Typography>
                <Grid container spacing={1.5}>
                  {page.useCases.map(([title, body]) => (
                    <Grid item xs={12} sm={6} key={title}>
                      <Card
                        elevation={0}
                        sx={{
                          height: "100%",
                          bgcolor: "#ffffff",
                          border: `1px solid ${colors.border}`,
                          boxShadow: "0 10px 24px rgba(15, 23, 42, 0.035)",
                        }}
                      >
                        <CardContent sx={{ p: 2.2 }}>
                          <Typography sx={{ color: colors.text, fontWeight: 900 }}>{title}</Typography>
                          <Typography sx={{ color: colors.secondary, lineHeight: 1.55, mt: 0.55 }}>{body}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </Grid>
          </Grid>
        </Container>

        <Box sx={{ bgcolor: colors.navy900, color: "#ffffff" }}>
          <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
            <Grid container spacing={{ xs: 4, md: 7 }}>
              <Grid item xs={12} md={6.5}>
                <Stack spacing={1.4}>
                  <Typography variant="overline" sx={{ color: "#8fd2c1", fontWeight: 900, letterSpacing: 0 }}>
                    Privacy and control
                  </Typography>
                  <Typography variant="h2" sx={{ color: "#ffffff", fontWeight: 950, lineHeight: 1.08, fontSize: { xs: "2rem", md: "2.55rem" } }}>
                    Visibility should feel chosen.
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.74)", lineHeight: 1.65 }}>
                    TrustBud separates report state from private detail. That lets users get the benefit of accountability without exposing sensitive notes, calendar details, or health data by default.
                  </Typography>
                  <Stack spacing={1.1} sx={{ pt: 1 }}>
                    {["Status-only visibility", "Private proof labels", "Sensitive habit protection"].map((item) => (
                      <Stack key={item} direction="row" spacing={1} alignItems="center">
                        <LockOutlinedIcon sx={{ color: "#8fd2c1", fontSize: 20 }} />
                        <Typography sx={{ color: "rgba(255,255,255,0.8)", fontWeight: 760 }}>{item}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12} md={5.5}>
                <Card
                  elevation={0}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.14)",
                    boxShadow: "0 18px 42px rgba(0,0,0,0.18)",
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography sx={{ color: "#ffffff", fontWeight: 900, mb: 1.5 }}>
                      Related TrustBud pages
                    </Typography>
                    <RelatedPages currentPath={page.path} />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Grid container spacing={{ xs: 4, md: 6 }}>
            <Grid item xs={12} md={4.5}>
              <Typography variant="overline" sx={{ color: colors.teal, fontWeight: 900, letterSpacing: 0 }}>
                FAQ
              </Typography>
              <Typography variant="h2" sx={{ color: colors.text, fontWeight: 950, lineHeight: 1.08, mt: 1, fontSize: { xs: "2rem", md: "2.45rem" } }}>
                Common questions
              </Typography>
            </Grid>
            <Grid item xs={12} md={7.5}>
              <Stack spacing={1.4}>
                {page.faqs.map(([question, answer]) => (
                  <Card
                    key={question}
                    elevation={0}
                    sx={{
                      bgcolor: "#ffffff",
                      border: `1px solid ${colors.border}`,
                      boxShadow: "0 10px 24px rgba(15, 23, 42, 0.035)",
                    }}
                  >
                    <CardContent sx={{ p: 2.2 }}>
                      <Typography sx={{ color: colors.text, fontWeight: 900 }}>{question}</Typography>
                      <Typography sx={{ color: colors.secondary, lineHeight: 1.6, mt: 0.65 }}>{answer}</Typography>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>

        <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 8 } }}>
          <Card elevation={0} sx={{ bgcolor: colors.navy900, color: "#ffffff", borderColor: colors.navy900 }}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={7.5}>
                  <Typography sx={{ color: "#ffffff", fontSize: { xs: "1.9rem", md: "2.45rem" }, lineHeight: 1.08, fontWeight: 950 }}>
                    Start with one goal, one report, and one person.
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.74)", mt: 1.3, lineHeight: 1.65 }}>
                    TrustBud is strongest when the first loop is simple enough to use today.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4.5}>
                  <Stack direction={{ xs: "column", sm: "row", md: "column" }} spacing={1.25}>
                    <Button component={RouterLink} to="/register" variant="contained" size="large" sx={{ bgcolor: "#ffffff", color: colors.navy900, "&:hover": { bgcolor: "#e8eef5" } }}>
                      Start your first habit
                    </Button>
                    <Button component={RouterLink} to="/" variant="outlined" size="large" sx={{ color: "#ffffff", borderColor: "rgba(255,255,255,0.34)" }}>
                      Back to home
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Container>
      </Box>

      <Divider />
      <Container maxWidth="lg" sx={{ py: 3.5 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }}>
          <Typography variant="body2" sx={{ color: colors.muted }}>
            © {year} TrustBud
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button component={RouterLink} to="/privacy">
              Privacy Policy
            </Button>
            <Button component={RouterLink} to="/terms">
              Terms of Use
            </Button>
            <Button component="a" href="mailto:support@trustbud.app">
              Contact Support
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}

export { SEO_LANDING_PAGES };
